import React, { useState } from 'react';
import HectorWidget from './components/HectorWidget';
import { ContactFormData, WidgetConfig } from './types';
import './index.css';

function App() {
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>({
    position: 'bottom',
    buttonText: 'Me faire appeler par un expert',
    buttonColor: '#1a1a1a'
  });

  const [submissions, setSubmissions] = useState<ContactFormData[]>([]);

  const handleFormSubmit = (data: ContactFormData) => {
    setSubmissions(prev => [...prev, data]);
    console.log('Nouvelle soumission:', data);
  };

  const togglePosition = () => {
    setWidgetConfig(prev => ({
      ...prev,
      position: prev.position === 'bottom' ? 'top' : 'bottom'
    }));
  };

  const changeColor = (color: string) => {
    setWidgetConfig(prev => ({
      ...prev,
      buttonColor: color
    }));
  };

  return (
    <div className="h-screen bg-gray-50 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-pink-400 to-orange-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-r from-indigo-400 to-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      </div>

      {/* Header */}
      <header className="relative bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Hector Widget
              </h1>
              <p className="text-gray-600 mt-1">
                Widget de contact moderne et intégrable
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full overflow-y-auto">
        {/* Configuration Panel */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Configuration du Widget
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position du bouton
              </label>
              <button
                onClick={togglePosition}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  widgetConfig.position === 'bottom'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {widgetConfig.position === 'bottom' ? 'En bas' : 'En haut'}
              </button>
            </div>

            {/* Couleur */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couleur du bouton
              </label>
              <div className="flex space-x-2">
                {['#1a1a1a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f5f5dc', '#c0c0c0'].map((color) => (
                  <button
                    key={color}
                    onClick={() => changeColor(color)}
                    className={`w-8 h-8 rounded-full border-2 ${
                      widgetConfig.buttonColor === color ? 'border-gray-400' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Changer la couleur en ${color}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Demo Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Instructions */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Comment utiliser le widget
            </h2>
            <div className="space-y-4 text-gray-600">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <p>Cliquez sur le bouton flottant pour ouvrir le formulaire</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <p>Remplissez tous les champs obligatoires</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <p>Cliquez sur "Envoyer le formulaire"</p>
              </div>
            </div>
          </div>

          {/* Submissions */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Soumissions ({submissions.length})
            </h2>
            {submissions.length === 0 ? (
              <p className="text-gray-500 italic">
                Aucune soumission pour le moment. Testez le widget !
              </p>
            ) : (
              <div className="space-y-3">
                {submissions.map((submission, index) => (
                  <div key={index} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">
                        {submission.prenom} {submission.nom}
                      </p>
                      <p className="text-gray-600">{submission.mail}</p>
                      <p className="text-gray-500 text-xs">
                        DPE: {submission.numeroDpe}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Integration Code */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6 mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Code d'intégration
          </h2>
          
          {/* Code JavaScript pour intégration directe */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
            <pre className="text-green-400 text-sm">
              <code>{`<!-- Intégration simple -->
<script src="https://cdn.hector-widget.com/v1/hector-widget.min.js"></script>
<script>
  HectorWidget.init({
    position: 'bottom',        // 'top' ou 'bottom'
    buttonColor: '#1a1a1a'     // Couleur personnalisée
  });
</script>`}</code>
            </pre>
          </div>
        </div>
      </main>

      {/* Widget */}
      <HectorWidget 
        config={widgetConfig}
        onSubmit={handleFormSubmit}
      />
    </div>
  );
}

export default App;
